import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_PATH } from './utils/basePath';
import LoansDashboard from './pages/LoansDashboard';
import LoginSSORedirect from './pages/LoginSSORedirect';
import LogoutComplete from './pages/LogoutComplete';
import LoanDetails from './pages/LoanDetails';
import FAQ from './pages/FAQ';

import SelectPaymentMethod from './components/SelectPaymentMethod';
import PaymentFlow from './pages/PaymentFlow';
import PaymentSuccess from './pages/LoanDetails/components/PaymentSuccess';
import './styles/fonts.css';
import './styles/index.css';
import { ThemeToggleProvider } from './ThemeToggleProvider';
import Header from './components/Header';
import { Box, Container, CircularProgress } from '@mui/material';
import SummaryTab from './pages/LoanDetails/components/SummaryTab';
import PaymentsTab from './pages/LoanDetails/components/PaymentsTab';
import RepaymentHistoryTab from './pages/LoanDetails/components/RepaymentHistoryTab';
import RateScheduleTab from './pages/LoanDetails/components/RateScheduleTab';
import ScheduleTab from './pages/LoanDetails/components/ScheduleTab';
import TransactionSummaryTab from './pages/LoanDetails/components/TransactionSummaryTab';
import BillsTab from './pages/LoanDetails/components/BillsTab';
import PayoffEstimatorTab from './pages/LoanDetails/components/PayoffEstimatorTab';
import PartiesTab from './pages/LoanDetails/components/PartiesTab';
import ProfileSettings from './pages/ProfileSettings';
import LoanStatementTab from './pages/LoanDetails/components/LoanStatementTab';

import useAuthCheck from './useAuthCheck';

// Configure axios to include credentials and base path
axios.defaults.withCredentials = true;
axios.defaults.baseURL = BASE_PATH;

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authenticated, loading } = useAuthCheck();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return authenticated ? <>{children}</> : <Navigate to="/login" />;
};

const App: React.FC = () => {
  return (
    <ThemeToggleProvider>
      <Router basename={BASE_PATH}>
        <Routes>
          <Route path="/login" element={<LoginSSORedirect />} />
          <Route path="/logout-complete" element={<LogoutComplete />} />
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <>
                  <Header />
                  <Container maxWidth={false} component="main" sx={{ pt: 4, pb: 4, minHeight: 'calc(100vh - 64px)' }}>
                    <Routes>
                      <Route path="/" element={<Navigate to="/loans" />} />
                      <Route path="/loans" element={<LoansDashboard />} />
                      <Route path="/loans/:loanId" element={<LoanDetails />}>
                        <Route index element={<SummaryTab />} />
                        <Route path="summary" element={<SummaryTab />} />
                        <Route path="payments" element={<PaymentsTab />} />
                        <Route path="repayment-history" element={<RepaymentHistoryTab />} />
                        <Route path="rate-schedule" element={<RateScheduleTab />} />
                        <Route path="schedule" element={<ScheduleTab />} />
                        <Route path="transaction-summary" element={<TransactionSummaryTab />} />
                        <Route path="bills" element={<BillsTab />} />
                        <Route path="loan-statement" element={<LoanStatementTab />} />
                        <Route path="payoff" element={<PayoffEstimatorTab />} />
                        <Route path="parties" element={<PartiesTab />} />
                      </Route>
                      <Route path="/loans/:loanId/payment-method" element={<SelectPaymentMethod />} />
                      <Route path="/loans/:loanId/payment-flow" element={<PaymentFlow />} />
                      <Route path="/payment-success" element={<PaymentSuccess />} />
                      <Route path="/profileSettings" element={<ProfileSettings />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/transactions" element={<TransactionSummaryTab />} />
                    </Routes>
                  </Container>
                </>
              </PrivateRoute>
            }
          />
        </Routes>
      </Router>
    </ThemeToggleProvider>
  );
};

export default App;
